import type { ServerAsset } from '@sequencehq/api/utils/commonModels'
import { Button } from '@sequencehq/core-components'
import { FilePickerButton } from '@sequencehq/core-components'
import { useUploadAssets } from 'Assets/hooks/useUploadAssets'
import { validatePickedFiles } from 'Assets/utils'
import { ComponentProps, FC, useEffect, useState } from 'react'

type Props = ComponentProps<typeof Button> & {
  onFilesPicked?: (files: File[]) => void
  onUploadSuccess: (asset: ServerAsset) => void | Promise<void>
  onUploadFailure?: (error: string) => void
  onAllUploadsComplete?: () => void
  accept: string
  multiple?: boolean
  maximumNumberOfFiles?: number
  maximumNumberOfFilesError?: string
}

export const UploadAssetButton: FC<Props> = ({
  onFilesPicked,
  onUploadSuccess,
  onUploadFailure,
  onAllUploadsComplete,
  maximumNumberOfFiles = Infinity,
  maximumNumberOfFilesError,
  ...props
}) => {
  const uploadAssets = useUploadAssets()

  const [files, setFiles] = useState<File[] | null>(null)

  const handleFilesPicked = (files: File[]) => {
    const validationError = validatePickedFiles({
      files,
      maximumNumberOfFiles,
      maximumNumberOfFilesError
    })

    if (validationError) {
      onUploadFailure?.(validationError)
      return
    }

    setFiles(files)
    onFilesPicked?.(files)
  }

  const uploadFiles = async (files: File[]) => {
    await uploadAssets({ files, onUploadSuccess, onUploadFailure })
    setFiles(null)
    onAllUploadsComplete?.()
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: on call when files changes
  useEffect(() => {
    if (!files || !files.length) return
    uploadFiles(files)
  }, [files])

  return <FilePickerButton {...props} onFilesPicked={handleFilesPicked} />
}
